import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRestaurantsV1OperationGroupServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'dev._base_domain_': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
      {
        srcPath: '/restaurants/v1/bulk/operation-groups',
        destPath: '/v1/bulk/operation-groups',
      },
      {
        srcPath: '/restaurants/v1/operation-groups',
        destPath: '/v1/operation-groups',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
      {
        srcPath: '/restaurants/v1/bulk/operation-groups',
        destPath: '/v1/bulk/operation-groups',
      },
      {
        srcPath: '/restaurants/v1/operation-groups',
        destPath: '/v1/operation-groups',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/restaurants-operation-group',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/restaurants/v1/operation-groups',
        destPath: '/v1/operation-groups',
      },
      {
        srcPath: '/restaurants/v1/bulk/operation-groups',
        destPath: '/v1/bulk/operation-groups',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_operation-groups';

/** Creates a OperationGroup. */
export function createOperationGroup(
  payload: object
): RequestOptionsFactory<any> {
  function __createOperationGroup({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'operationGroup.createdDate' },
          { path: 'operationGroup.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.CreateOperationGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/operation-groups',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operationGroup.createdDate' },
              { path: 'operationGroup.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createOperationGroup;
}

/** Retrieves a OperationGroup. */
export function getOperationGroup(payload: object): RequestOptionsFactory<any> {
  function __getOperationGroup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'GET' as any,
      methodFqn: 'wix.restaurants.v1.OperationGroupService.GetOperationGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/operation-groups/{operationGroupId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operationGroup.createdDate' },
              { path: 'operationGroup.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getOperationGroup;
}

/**
 * Updates a OperationGroup.
 *
 *
 * Each time the OperationGroup is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the OperationGroup.
 * This ensures you're working with the latest OperationGroup
 * and prevents unintended overwrites.
 */
export function updateOperationGroup(
  payload: object
): RequestOptionsFactory<any> {
  function __updateOperationGroup({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'operationGroup.createdDate' },
          { path: 'operationGroup.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.UpdateOperationGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/operation-groups/{operationGroup.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operationGroup.createdDate' },
              { path: 'operationGroup.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateOperationGroup;
}

/**
 * Deletes a OperationGroup.
 *
 *
 * Deleting a OperationGroup permanently removes them from the OperationGroup List.
 */
export function deleteOperationGroup(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteOperationGroup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.DeleteOperationGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/operation-groups/{operationGroupId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteOperationGroup;
}

/** Retrieves a list of OperationGroups. */
export function queryOperationGroups(
  payload: object
): RequestOptionsFactory<any> {
  function __queryOperationGroups({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.QueryOperationGroups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/operation-groups/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'operationGroups.createdDate' },
              { path: 'operationGroups.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixRestaurantsV1OperationGroupServiceUrl({
            protoPath: '/v1/operation-groups/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryOperationGroups;
}

/** Create multiple OperationGroups in a single request. Works synchronously. */
export function bulkCreateOperationGroups(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateOperationGroups({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'operationGroups.createdDate' },
          { path: 'operationGroups.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.BulkCreateOperationGroups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/bulk/operation-groups/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateOperationGroups;
}

/** Update multiple OperationGroups in a single request. Works synchronously. */
export function bulkUpdateOperationGroups(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateOperationGroups({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'operationGroups.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'operationGroups.operationGroup.createdDate' },
          { path: 'operationGroups.operationGroup.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.BulkUpdateOperationGroups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/bulk/operation-groups/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateOperationGroups;
}

/** Delete multiple OperationGroups in a single request. Works synchronously. */
export function bulkDeleteOperationGroups(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteOperationGroups({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.BulkDeleteOperationGroups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/bulk/operation-groups/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteOperationGroups;
}

/**
 * Synchronously update tags on multiple operation groups.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateOperationGroupTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateOperationGroupTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.BulkUpdateOperationGroupTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/bulk/operation-groups/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateOperationGroupTags;
}

/**
 * Asynchronously update tags on multiple operation groups according to the specified filter.
 * If a filter isn't specified, this method updates all operation groups.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateOperationGroupTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateOperationGroupTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.operation_group',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.OperationGroupService.BulkUpdateOperationGroupTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1OperationGroupServiceUrl({
        protoPath: '/v1/bulk/operation-groups/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateOperationGroupTagsByFilter;
}
